import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  Search,
  Filter,
  Download,
  Upload,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Play,
  Loader2,
  X,
  FileSpreadsheet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Platform colors and logos
const platformConfig = {
  Cashify: { color: 'bg-green-500', textColor: 'text-green-600', bgLight: 'bg-green-50', logo: '💚' },
  Budli: { color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50', logo: '💙' },
  RecycleDevice: { color: 'bg-purple-500', textColor: 'text-purple-600', bgLight: 'bg-purple-50', logo: '💜' }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    SUCCESS: { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Success' },
    NOT_FOUND: { icon: XCircle, color: 'text-gray-500 bg-gray-50', label: 'Not Found' },
    ERROR: { icon: AlertCircle, color: 'text-red-600 bg-red-50', label: 'Error' },
    PENDING: { icon: Clock, color: 'text-yellow-600 bg-yellow-50', label: 'Pending' }
  };
  
  const config = statusConfig[status] || statusConfig.PENDING;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, subValue, icon: Icon, color = 'blue', trend, trendValue }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          {trend === 'up' ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
};

// Platform Coverage Bar
const PlatformCoverageBar = ({ stats }) => {
  const platforms = [
    { key: 'with_cashify_mbp', name: 'Cashify', color: 'bg-green-500' },
    { key: 'with_budli_mbp', name: 'Budli', color: 'bg-blue-500' },
    { key: 'with_recycledevice_mbp', name: 'RecycleDevice', color: 'bg-purple-500' }
  ];
  
  const total = stats?.total || 1;
  
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Platform Coverage</h3>
      <div className="space-y-4">
        {platforms.map(platform => {
          const count = stats?.[platform.key] || 0;
          const percentage = ((count / total) * 100).toFixed(1);
          
          return (
            <div key={platform.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                <span className="text-sm text-gray-500">{count} / {total} ({percentage}%)</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div 
                  className={`${platform.color} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Product Price Comparison Card
const ProductPriceCard = ({ product, onRefresh, isRefreshing }) => {
  const [expanded, setExpanded] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const fetchComparison = async () => {
    if (comparison) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/price-scraper/compare/${product.id}`);
      if (res.ok) {
        const data = await res.json();
        setComparison(data);
      }
    } catch (err) {
      console.error('Error fetching comparison:', err);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    if (expanded && !comparison) {
      fetchComparison();
    }
  }, [expanded]);
  
  const maxPrice = Math.max(
    product.cashify_mbp || 0,
    product.budli_mbp || 0,
    product.recycledevice_mbp || 0
  );
  
  const priceCount = [
    product.cashify_mbp,
    product.budli_mbp,
    product.recycledevice_mbp
  ].filter(p => p && p > 0).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
              📱
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">{product.model_name}</h4>
              <p className="text-sm text-gray-500">{product.sku_code}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Quick Price Overview */}
            <div className="hidden md:flex items-center gap-4">
              {product.cashify_mbp > 0 && (
                <div className="text-center">
                  <p className="text-xs text-gray-500">Cashify</p>
                  <p className="font-semibold text-green-600">₹{product.cashify_mbp?.toLocaleString()}</p>
                </div>
              )}
              {product.budli_mbp > 0 && (
                <div className="text-center">
                  <p className="text-xs text-gray-500">Budli</p>
                  <p className="font-semibold text-blue-600">₹{product.budli_mbp?.toLocaleString()}</p>
                </div>
              )}
              {product.recycledevice_mbp > 0 && (
                <div className="text-center">
                  <p className="text-xs text-gray-500">RecycleDevice</p>
                  <p className="font-semibold text-purple-600">₹{product.recycledevice_mbp?.toLocaleString()}</p>
                </div>
              )}
            </div>
            
            {/* Max Price Badge */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg">
              <p className="text-xs opacity-80">Best Price</p>
              <p className="font-bold text-lg">₹{maxPrice.toLocaleString()}</p>
            </div>
            
            {/* Coverage Badge */}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              priceCount === 3 ? 'bg-green-100 text-green-700' :
              priceCount === 2 ? 'bg-yellow-100 text-yellow-700' :
              priceCount === 1 ? 'bg-orange-100 text-orange-700' :
              'bg-red-100 text-red-700'
            }`}>
              {priceCount}/3 platforms
            </div>
            
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : comparison ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {comparison.prices?.map((price, idx) => (
                <div 
                  key={price.platform}
                  className={`p-4 rounded-xl border-2 ${
                    idx === 0 ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{platformConfig[price.platform]?.logo}</span>
                      <span className="font-semibold text-gray-800">{price.platform}</span>
                    </div>
                    {idx === 0 && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">BEST</span>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-gray-800 mb-2">
                    ₹{price.mbp?.toLocaleString()}
                  </p>
                  <StatusBadge status={price.status} />
                  {price.url && (
                    <a 
                      href={price.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      View on {price.platform} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {comparison?.last_scraped && (
                <span>Last updated: {new Date(comparison.last_scraped).toLocaleString()}</span>
              )}
            </div>
            <button
              onClick={() => onRefresh(product.id)}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isRefreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh Prices
            </button>
          </div>
          
          {/* Price Spread */}
          {comparison?.price_spread > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Price Spread:</strong> ₹{comparison.price_spread.toLocaleString()} difference between highest and lowest offers
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Scraper Logs Table
const ScraperLogsTable = ({ logs }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-800">Recent Scraper Runs</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Products</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {logs.map((log) => (
              <tr key={log.log_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {log.run_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{log.platform}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={log.status} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{log.total_products}</td>
                <td className="px-4 py-3 text-sm text-green-600">{log.success_count}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {new Date(log.started_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Main Component
const BuybackPriceComparison = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingProduct, setRefreshingProduct] = useState(null);
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  
  // Bulk URL Import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/price-scraper/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };
  
  // Fetch products with prices
  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/api/products?limit=100`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || data || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  
  // Fetch logs
  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API}/api/price-scraper/logs?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchProducts(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, []);
  
  // Refresh single product
  const handleRefreshProduct = async (productId) => {
    setRefreshingProduct(productId);
    try {
      const res = await fetch(`${API}/api/price-scraper/scrape/single/${productId}`, {
        method: 'POST'
      });
      if (res.ok) {
        // Refresh data
        await Promise.all([fetchStats(), fetchProducts(), fetchLogs()]);
      }
    } catch (err) {
      console.error('Error refreshing product:', err);
    }
    setRefreshingProduct(null);
  };
  
  // Trigger bulk refresh
  const handleBulkRefresh = async () => {
    setBulkRefreshing(true);
    try {
      const res = await fetch(`${API}/api/price-scraper/scrape/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        // Show notification
        alert('Bulk price refresh started in background. Check logs for progress.');
        await fetchLogs();
      }
    } catch (err) {
      console.error('Error triggering bulk refresh:', err);
    }
    setBulkRefreshing(false);
  };
  
  // Parse CSV for preview
  const handleParseCSV = async () => {
    if (!csvData.trim()) {
      alert('Please paste CSV data first');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/mbp-platforms/parse-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_data: csvData })
      });
      const data = await res.json();
      setImportPreview(data);
    } catch (err) {
      console.error('Error parsing CSV:', err);
      alert('Error parsing CSV data');
    }
  };
  
  // Execute bulk URL import
  const handleBulkImport = async () => {
    if (!importPreview?.preview?.length) {
      alert('No valid data to import');
      return;
    }
    
    setImporting(true);
    try {
      const items = importPreview.preview.map(row => ({
        sku_code: row.sku_code,
        storage: row.storage,
        cashify_url: row.cashify_url || null,
        budli_url: row.budli_url || null,
        recycledevice_url: row.recycledevice_url || null
      }));
      
      const res = await fetch(`${API}/api/mbp-platforms/bulk-import-urls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      const data = await res.json();
      setImportResult(data);
      
      if (data.success) {
        // Refresh stats
        await fetchStats();
      }
    } catch (err) {
      console.error('Error importing URLs:', err);
      alert('Error importing URLs');
    }
    setImporting(false);
  };
  
  // Reset import modal
  const resetImportModal = () => {
    setCsvData('');
    setImportPreview(null);
    setImportResult(null);
    setShowBulkImport(false);
  };
  
  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.model_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.sku_code?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (filterPlatform === 'all') return true;
    if (filterPlatform === 'cashify') return p.cashify_mbp > 0;
    if (filterPlatform === 'budli') return p.budli_mbp > 0;
    if (filterPlatform === 'recycledevice') return p.recycledevice_mbp > 0;
    if (filterPlatform === 'missing') {
      return !p.cashify_mbp && !p.budli_mbp && !p.recycledevice_mbp;
    }
    return true;
  });
  
  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Price Comparison Dashboard</h1>
            <p className="text-gray-500 mt-1">Track competitor prices across Cashify, Budli & RecycleDevice</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBulkImport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
            >
              <Upload className="w-4 h-4" />
              Bulk Import URLs
            </button>
            <button
              onClick={handleBulkRefresh}
              disabled={bulkRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow-md"
            >
              {bulkRefreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Refresh All Prices
            </button>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Total Products"
            value={stats?.products?.total || 0}
            icon={BarChart3}
            color="blue"
          />
          <StatsCard
            title="With Price Data"
            value={stats?.products?.with_any_mbp || 0}
            subValue={`${stats?.products?.coverage_percent || 0}% coverage`}
            icon={CheckCircle}
            color="green"
          />
          <StatsCard
            title="Price History"
            value={stats?.history?.total_entries || 0}
            subValue="Total entries tracked"
            icon={TrendingUp}
            color="purple"
          />
          <StatsCard
            title="Last Weekly Run"
            value={stats?.last_weekly_run ? 'Completed' : 'Pending'}
            subValue={stats?.last_weekly_run?.started_at ? new Date(stats.last_weekly_run.started_at).toLocaleDateString() : 'Not yet run'}
            icon={Clock}
            color="orange"
          />
        </div>
        
        {/* Platform Coverage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PlatformCoverageBar stats={stats?.products} />
          
          {/* Quick Stats by Platform */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Platform Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💚</span>
                  <div>
                    <p className="font-semibold text-gray-800">Cashify</p>
                    <p className="text-sm text-gray-500">{stats?.products?.with_cashify_mbp || 0} products</p>
                  </div>
                </div>
                <StatusBadge status="SUCCESS" />
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💙</span>
                  <div>
                    <p className="font-semibold text-gray-800">Budli</p>
                    <p className="text-sm text-gray-500">{stats?.products?.with_budli_mbp || 0} products</p>
                  </div>
                </div>
                <StatusBadge status="SUCCESS" />
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💜</span>
                  <div>
                    <p className="font-semibold text-gray-800">RecycleDevice</p>
                    <p className="text-sm text-gray-500">{stats?.products?.with_recycledevice_mbp || 0} products</p>
                  </div>
                </div>
                <StatusBadge status="SUCCESS" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Search and Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products by name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterPlatform}
                onChange={(e) => setFilterPlatform(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Products</option>
                <option value="cashify">Has Cashify Price</option>
                <option value="budli">Has Budli Price</option>
                <option value="recycledevice">Has RecycleDevice Price</option>
                <option value="missing">Missing All Prices</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Products List */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Products ({filteredProducts.length})
          </h3>
          {filteredProducts.map(product => (
            <ProductPriceCard
              key={product.id}
              product={product}
              onRefresh={handleRefreshProduct}
              isRefreshing={refreshingProduct === product.id}
            />
          ))}
        </div>
        
        {/* Scraper Logs */}
        {logs.length > 0 && <ScraperLogsTable logs={logs} />}
        
        {/* Bulk URL Import Modal */}
        {showBulkImport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl">
                    <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Bulk Import URLs</h2>
                    <p className="text-sm text-gray-500">Import platform URLs from CSV data</p>
                  </div>
                </div>
                <button 
                  onClick={resetImportModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="p-5 overflow-y-auto max-h-[60vh]">
                {!importResult ? (
                  <>
                    {/* Instructions */}
                    <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="text-sm text-blue-800 font-medium mb-2">CSV Format (tab or comma separated):</p>
                      <code className="text-xs bg-white px-2 py-1 rounded text-blue-700 block">
                        SKU Code, Storage, Cashify URL, Budli URL, RecycleDevice URL
                      </code>
                      <p className="text-xs text-blue-600 mt-2">
                        Example: IPHONE-IPHONE13, 128GB, https://cashify.in/..., https://budli.in/..., https://recycledevice.com/...
                      </p>
                    </div>
                    
                    {/* CSV Input */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Paste CSV Data
                      </label>
                      <textarea
                        value={csvData}
                        onChange={(e) => setCsvData(e.target.value)}
                        placeholder="SKU Code&#9;Storage&#9;Cashify URL&#9;Budli URL&#9;RecycleDevice URL
IPHONE-IPHONE13&#9;128GB&#9;https://cashify.in/sell-old-mobile-phone/used-apple-iphone-13&#9;https://budli.in/sell-used-mobile-phones/apple-iphone-13&#9;https://recycledevice.com/sell-mobile/apple/apple-iphone-13"
                        className="w-full h-40 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Parse Button */}
                    <button
                      onClick={handleParseCSV}
                      className="mb-4 flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Search className="w-4 h-4" />
                      Preview Import
                    </button>
                    
                    {/* Preview Table */}
                    {importPreview && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-3 bg-gray-50 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-700">
                            {importPreview.success 
                              ? `Preview: ${importPreview.preview?.length || 0} rows parsed`
                              : `Error: ${importPreview.message}`
                            }
                          </p>
                          {importPreview.columns_detected && (
                            <p className="text-xs text-gray-500 mt-1">
                              Columns detected: {importPreview.columns_detected.join(', ')}
                            </p>
                          )}
                        </div>
                        {importPreview.preview?.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Storage</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Model</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">URLs</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {importPreview.preview.map((row, idx) => (
                                  <tr key={idx} className={row.product_found ? '' : 'bg-red-50'}>
                                    <td className="px-3 py-2 font-mono text-xs">{row.sku_code}</td>
                                    <td className="px-3 py-2">{row.storage}</td>
                                    <td className="px-3 py-2">{row.model_name || '-'}</td>
                                    <td className="px-3 py-2">
                                      {row.product_found ? (
                                        <span className="text-green-600 text-xs flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" /> Found
                                        </span>
                                      ) : (
                                        <span className="text-red-600 text-xs flex items-center gap-1">
                                          <XCircle className="w-3 h-3" /> Not Found
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex gap-1">
                                        {row.cashify_url && <span className="w-2 h-2 bg-green-500 rounded-full" title="Cashify"></span>}
                                        {row.budli_url && <span className="w-2 h-2 bg-blue-500 rounded-full" title="Budli"></span>}
                                        {row.recycledevice_url && <span className="w-2 h-2 bg-purple-500 rounded-full" title="RecycleDevice"></span>}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* Import Results */
                  <div className="space-y-4">
                    <div className={`p-4 rounded-xl ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {importResult.message}
                      </p>
                    </div>
                    
                    {/* Results Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-green-600">{importResult.results?.success || 0}</p>
                        <p className="text-sm text-green-700">Successful</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-red-600">{importResult.results?.failed || 0}</p>
                        <p className="text-sm text-red-700">Failed</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-2xl font-bold text-gray-600">{importResult.results?.skipped || 0}</p>
                        <p className="text-sm text-gray-700">Skipped</p>
                      </div>
                    </div>
                    
                    {/* Details Table */}
                    {importResult.results?.details?.length > 0 && (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-3 bg-gray-50 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-700">Import Details</p>
                        </div>
                        <div className="overflow-x-auto max-h-60">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Storage</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {importResult.results.details.map((detail, idx) => (
                                <tr key={idx} className={
                                  detail.status === 'SUCCESS' ? 'bg-green-50' :
                                  detail.status === 'SKIPPED' ? 'bg-gray-50' : 'bg-red-50'
                                }>
                                  <td className="px-3 py-2 font-mono text-xs">{detail.sku_code}</td>
                                  <td className="px-3 py-2">{detail.storage}</td>
                                  <td className="px-3 py-2">
                                    <StatusBadge status={detail.status} />
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-600">
                                    {detail.status === 'SUCCESS' 
                                      ? `URLs: ${detail.urls_added?.join(', ')} (${detail.variants_updated} variants)`
                                      : detail.message
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50">
                {!importResult ? (
                  <>
                    <button
                      onClick={resetImportModal}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkImport}
                      disabled={!importPreview?.preview?.length || importing}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Import URLs
                    </button>
                  </>
                ) : (
                  <button
                    onClick={resetImportModal}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BuybackPriceComparison;
